import { Check, LoaderCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { previewVat, type SupplierPick, type VatPreview } from './api';
import { splitVat, vatRule } from './countries';

/** What the register said about the number typed, for the window to act on. */
export type VatState =
  | { readonly kind: 'empty' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'checked'; readonly preview: VatPreview }
  /** 422 VAT_INVALID: «Crea» would be refused too. */
  | { readonly kind: 'form-invalid' }
  /** VIES did not answer: the creation still goes, the check stays «unchecked». */
  | { readonly kind: 'unavailable' };

/** The supplier with this VAT number already in the catalog, if the check found one. */
export function existingOf(state: VatState): SupplierPick | null {
  return state.kind === 'checked' ? state.preview.existing : null;
}

/** A pause after typing before the check, so one request goes per number, not per digit. */
export const VAT_CHECK_DELAY_MS = 500;

/**
 * «Partita IVA · facoltativa» (boards 1.5–1.8): the number is checked as it is typed
 * (`previewSupplierVat`), which also says whether another supplier already has it. The hint under
 * it follows the country: VIES in the EU, «not yet» for the United Kingdom and Switzerland,
 * optional elsewhere.
 */
export function VatField({
  id,
  country,
  value,
  onChange,
  state,
  onState,
}: {
  readonly id: string;
  readonly country: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly state: VatState;
  readonly onState: (state: VatState) => void;
}) {
  const { t } = useTranslation('suppliers');
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const run = useRef(0);

  useEffect(() => {
    const current = ++run.current;
    const split = splitVat(value, country);
    if (split === null || split.vat.length < 2) {
      onStateRef.current({ kind: 'empty' });
      return;
    }
    onStateRef.current({ kind: 'checking' });
    const timer = setTimeout(() => {
      void previewVat(split.vatCountry, split.vat).then((outcome) => {
        if (current !== run.current) return;
        if (outcome.kind === 'checked') onStateRef.current(outcome);
        else if (outcome.kind === 'invalid') onStateRef.current({ kind: 'form-invalid' });
        else onStateRef.current({ kind: 'unavailable' });
      });
    }, VAT_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [value, country]);

  const hintId = `${id}-hint`;
  const prefix = splitVat(value, country)?.vatCountry ?? (country === 'CH' ? 'CH' : country);
  const verified = state.kind === 'checked' && state.preview.identifierCheck === 'verified';
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold">
        {t('picker.create.vat')}{' '}
        <span className="text-muted-foreground font-normal">{t('picker.create.optional')}</span>
      </label>
      <div className="relative">
        <span
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs font-bold"
        >
          {prefix}
        </span>
        <Input
          id={id}
          value={value}
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('picker.create.vatPlaceholder')}
          aria-describedby={hintId}
          aria-invalid={state.kind === 'form-invalid' || undefined}
          className="min-h-11 pr-10 pl-10"
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="absolute top-1/2 right-3 -translate-y-1/2">
          {state.kind === 'checking' && (
            <LoaderCircle
              aria-hidden="true"
              className="text-muted-foreground size-4 animate-spin"
            />
          )}
          {verified && <Check aria-hidden="true" className="text-success size-4" />}
        </span>
      </div>
      <VatHint id={hintId} country={country} state={state} />
    </div>
  );
}

function VatHint({
  id,
  country,
  state,
}: {
  readonly id: string;
  readonly country: string;
  readonly state: VatState;
}) {
  const { t } = useTranslation('suppliers');
  const rule = vatRule(country);
  let tone: 'muted' | 'ok' | 'warn' | 'error' = 'muted';
  let text: string;
  if (state.kind === 'checking') {
    text = t('picker.vat.checking');
  } else if (state.kind === 'form-invalid') {
    tone = 'error';
    text = t('picker.vat.formInvalid');
  } else if (state.kind === 'unavailable') {
    tone = 'warn';
    text = t('picker.vat.viesDown');
  } else if (state.kind === 'checked' && state.preview.identifierCheck === 'verified') {
    tone = 'ok';
    text = state.preview.name
      ? t('picker.vat.verified', { name: state.preview.name })
      : t('picker.vat.verifiedNoName');
  } else if (state.kind === 'checked' && state.preview.identifierCheck === 'invalid') {
    tone = 'warn';
    text = t('picker.vat.viesInvalid');
  } else if (rule === 'uk') {
    text = t('picker.vat.uk');
  } else if (rule === 'ch') {
    text = t('picker.vat.ch');
  } else if (rule === 'optional') {
    text = t('picker.vat.elsewhere');
  } else {
    text = t('picker.vat.optional');
  }
  return (
    <small
      id={id}
      aria-live="polite"
      className={cn(
        'text-xs',
        tone === 'muted' && 'text-muted-foreground',
        tone === 'ok' && 'text-success',
        tone === 'warn' && 'text-warning',
        tone === 'error' && 'text-destructive',
      )}
    >
      {text}
    </small>
  );
}
